/*
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 *
 */

import { upsert } from "@/io.ox/office/tk/algorithms/map";

// public functions ===========================================================

/**
 * Creates an iterator that represents an empty data source.
 *
 * @returns
 *  A new empty iterator.
 */
export function *empty<VT>(): IterableIterator<VT> {
    // intentionally empty
}

/**
 * Creates an iterator that will visit a single value.
 *
 * @param value
 *  The value to be visited by the iterator.
 *
 * @yields
 *  The passed value (once).
 */
export function *once<VT>(value: VT): IterableIterator<VT> {
    yield value;
}

/**
 * Creates an infinite iterator that will visit the same value forever.
 *
 * @param [value]
 *  The value to be visited forever.
 *
 * @yields
 *  The passed value (forever).
 */
export function *forever<VT>(value: VT): IterableIterator<VT> {
    for (;;) {
        yield value;
    }
}

/**
 * Creates an infinite iterator that always yields the first element of a data
 * source. Intended for consumers that modify the data source in some way
 * (otherwise using the iterator would create an endless loop).
 *
 * @param source
 *  The data source to be visited.
 *
 * @yields
 *  Always the first element of the data source.
 */
export function *yieldFirst<VT>(source: Iterable<VT>): IterableIterator<VT> {
    for (;;) {
        const result = source[Symbol.iterator]().next();
        if (result.done) { break; }
        yield result.value;
    }
}

/**
 * Creates an iterator for a sequence of numbers, similar to a `for` loop.
 *
 * @param begin
 *  The first number to be generated by the iterator.
 *
 * @param end
 *  The upper (lower) boundary to stop iteration for (exclusive).
 *
 * @param [step=1]
 *  The step size for the generated numbers.
 *
 * @yields
 *  A sequence of numbers.
 */
export function *sequence(begin: number, end: number, step = 1): IterableIterator<number> {
    if (step < 0) {
        for (let i = begin; i > end; i += step) {
            yield i;
        }
    } else {
        for (let i = begin; i < end; i += step) {
            yield i;
        }
    }
}

/**
 * Creates an iterator for a closed interval of integers.
 *
 * @param lower
 *  The lower boundary of the index interval.
 *
 * @param upper
 *  The upper boundary of the index interval (inclusive).
 *
 * @param [reverse]
 *  If set to `true`, the indexes will be generated in reversed order, from
 *  `upper` to `lower`.
 *
 * @yields
 *  A closed interval of integers.
 */
export function *interval(lower: number, upper: number, reverse = false): IterableIterator<number> {
    yield* sequence(reverse ? upper : lower, reverse ? (lower - 1) : (upper + 1), reverse ? -1 : 1);
}

// aggregation algorithms -----------------------------------------------------

/**
 * Returns whether at least one value in an iterable passes a truth test.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The predicate callback function invoked for the values. Returns whether the
 *  value passes the test.
 *
 * @returns
 *  Whether at least one value passes the truth test.
 */
export function some<VT>(source: Iterable<VT>, fn: (value: VT) => boolean): boolean {
    for (const value of source) {
        if (fn(value)) { return true; }
    }
    return false;
}

/**
 * Returns whether all values in an iterable pass a truth test.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The predicate callback function invoked for the values. Returns whether the
 *  value passes the test.
 *
 * @returns
 *  Whether all values pass the truth test.
 */
export function every<VT>(source: Iterable<VT>, fn: (value: VT) => boolean): boolean {
    for (const value of source) {
        if (!fn(value)) { return false; }
    }
    return true;
}

/**
 * Invokes a callback function for all values in an iterable, and reduces them
 * to a single result value.
 *
 * @param source
 *  The iterable data source.
 *
 * @param initial
 *  The initial result value passed to the first invocation of the callback
 *  function (if the data source is empty, this value becomes the final result
 *  value immediately).
 *
 * @param fn
 *  The aggregator callback function that will be invoked for each value in the
 *  data source. The return value of the function will become the new result
 *  value that will be passed with the next value. If the function returns
 *  `undefined`, the current result value passed to the function will be used
 *  (useful for e.g. aggregating to arrays or other containers).
 *
 * @returns
 *  The final result (the last return value of the callback function).
 */
export function reduce<RT, VT>(source: Iterable<VT>, initial: RT, fn: (result: RT, value: VT) => RT | void): RT {
    for (const value of source) {
        const result = fn(initial, value);
        if (result !== undefined) { initial = result; }
    }
    return initial;
}

/**
 * Counts the values in an iterable.
 *
 * @param source
 *  The iterable data source.
 *
 * @returns
 *  The number of values in the data source.
 */
export function size<VT>(source: Iterable<VT>): number {
    let result = 0;
    for (const _value of source) {
        result += 1;
    }
    return result;
}

/**
 * Counts the values in an iterable that pass a truth test.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  A predicate callback function invoked for the values. Returns whether the
 *  value shall be counted.
 *
 * @returns
 *  The number of values passing the truth test.
 */
export function count<VT>(source: Iterable<VT>, fn: (value: VT) => boolean): number {
    let result = 0;
    for (const value of source) {
        if (fn(value)) { result += 1; }
    }
    return result;
}

/**
 * Distributes the elements of an iterable into a map of arrays that are keyed
 * by the return value of a callback function.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The callback function invoked for the values. The return value will be used
 *  as map key for the result. Return `undefined` to skip the value.
 *
 * @returns
 *  A map with multiple arrays containing the values grouped by the results of
 *  the callback function.
 */
export function group<KT, VT>(source: Iterable<VT>, fn: (value: VT) => Opt<KT>): Map<KT, VT[]> {
    const result = new Map<KT, VT[]>();
    for (const value of source) {
        const key = fn(value);
        if (key !== undefined) {
            upsert(result, key, () => []).push(value);
        }
    }
    return result;
}

/**
 * Distributes the elements of an iterable into a pair of arrays according to
 * the return value of a predicate callback function.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The predicate callback function invoked for the values. The return value
 *  will be used as pair element index (`false`: first element, `true`: second
 *  element).
 *
 * @returns
 *  A pair of arrays containing the values split by the results of the callback
 *  function.
 */
export function split<VT>(source: Iterable<VT>, fn: (value: VT) => boolean): Pair<VT[]> {
    const result: Pair<VT[]> = [[], []];
    for (const value of source) {
        result[fn(value) ? 1 : 0].push(value);
    }
    return result;
}

// find algorithms ------------------------------------------------------------

/**
 * Returns the next value from an iterator.
 *
 * @param iterator
 *  The iterator to shift a value from.
 *
 * @returns
 *  The next value from the iterator.
 */
export function shift<VT>(iterator: Iterator<VT>): Opt<VT> {
    const result = iterator.next();
    return result.done ? undefined : result.value;
}

/**
 * Returns the first value of an iterable data source.
 *
 * @param source
 *  The iterable data source.
 *
 * @returns
 *  The first value of the data source.
 */
export function first<VT>(source: Iterable<VT>): Opt<VT> {
    return shift(source[Symbol.iterator]());
}

/**
 * Returns the first value in an iterable that passes a truth test.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  A predicate callback function invoked for the values. Returns whether the
 *  value passes the test.
 *
 * @returns
 *  The first matching value; or `undefined`, if no matching value has been
 *  found.
 */
export function find<VT>(source: Iterable<VT>, fn: (value: VT) => boolean): Opt<VT> {
    for (const value of source) {
        if (fn(value)) { return value; }
    }
    return undefined;
}

/**
 * Returns the last value in an iterable that passes a truth test.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  A predicate callback function invoked for the values. Returns whether the
 *  value passes the test.
 *
 * @returns
 *  The last matching value; or `undefined`, if no matching value has been
 *  found.
 */
export function findLast<VT>(source: Iterable<VT>, fn: (value: VT) => boolean): Opt<VT> {
    let result: Opt<VT>;
    for (const value of source) {
        if (fn(value)) { result = value; }
    }
    return result;
}

/**
 * Returns the first value returned from the callback function that is not
 * `undefined`. Works similar to `find` but allows to create a new value
 * directly inside the callback function.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The callback function invoked for the values. The first return value that
 *  is not `undefined` will be returned immediately.
 *
 * @returns
 *  The first return value of the callback function that is not `undefined`.
 */
export function mapFirst<VT, RT>(source: Iterable<VT>, fn: (value: VT) => Opt<RT>): Opt<RT> {
    for (const value of source) {
        const result = fn(value);
        if (result !== undefined) { return result; }
    }
    return undefined;
}

/**
 * Returns the last value returned from the callback function that is not
 * `undefined`. Works similar to `findLast` but allows to create a new value
 * directly inside the callback function.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The callback function invoked for the values. The last return value that is
 *  not `undefined` will be returned immediately.
 *
 * @returns
 *  The last return value of the callback function that is not `undefined`.
 */
export function mapLast<VT, RT>(source: Iterable<VT>, fn: (value: VT) => Opt<RT>): Opt<RT> {
    let result: Opt<RT>;
    for (const value of source) {
        const next = fn(value);
        if (next !== undefined) { result = next; }
    }
    return result;
}

/**
 * Returns the value in an iterable that is the nearest to a given offset,
 * according to the results of a callback function.
 *
 * @param source
 *  The iterable data source.
 *
 * @param offset
 *  The target offset.
 *
 * @param fn
 *  The callback function returning an offset for the values.
 *
 * @returns
 *  The value that is nearest to the given offset, or `undefined` for empty
 *  sources.
 */
export function nearest<VT>(source: Iterable<VT>, offset: number, fn: (value: VT) => number): Opt<VT> {
    let minimum = Infinity;
    let result: Opt<VT>;
    for (const value of source) {
        const diff = Math.abs(fn(value) - offset);
        if (diff < minimum) {
            minimum = diff;
            result = value;
        }
    }
    return result;
}

// transformation -------------------------------------------------------------

/**
 * Creates a new iterator that generates zero-based indexes for the values of
 * an iterable, like `Array#entries`.
 *
 * @param source
 *  The iterable data source.
 *
 * @yields
 *  Pairs with a zero-based index, and the values of the data source, like
 *  `Array#entries`.
 */
export function *indexed<VT>(source: Iterable<VT>): IndexedIterator<VT> {
    let index = 0;
    for (const value of source) {
        yield [index, value];
        index += 1;
    }
}

/**
 * Creates a new iterator that chains multiple iterables in order.
 *
 * @param sources
 *  The iterable data sources.
 *
 * @yields
 *  The values of all iterable data sources.
 */
export function *chain<VT>(...sources: Array<Iterable<VT>>): IterableIterator<VT> {
    for (const source of sources) {
        yield* source;
    }
}

/**
 * Creates a new iterator that flattens multiple iterables in order.
 *
 * @param sources
 *  An iterable data source that provides nested iterable value sources.
 *
 * @param [fn]
 *  A mapper function that converts the values of the data source to nested
 *  iterable value sources.
 *
 * @returns
 *  A new iterator visiting the values of all nested iterable data sources.
 */
export function flatten<VT>(sources: Iterable<Iterable<VT>>): IterableIterator<VT>;
export function flatten<VT, UT>(sources: Iterable<UT>, fn: (source: UT) => Iterable<VT>): IterableIterator<VT>;
// implementation
export function *flatten<VT>(sources: Iterable<unknown>, fn?: (source: unknown) => Iterable<VT>): IterableIterator<VT> {
    for (const source of sources) {
        yield* fn ? fn(source) : (source as Iterable<VT>);
    }
}

/**
 * Creates a new iterator visiting only the values of an iterable that pass a
 * truth test.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The predicate callback function invoked for the values.
 *
 * @yields
 *  The matching values of the data source.
 */
export function *filter<VT>(source: Iterable<VT>, fn: (value: VT) => boolean): IterableIterator<VT> {
    for (const value of source) {
        if (fn(value)) { yield value; }
    }
}

/**
 * Creates a new iterator transforming the values of an iterable.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The transformation callback function invoked for the values. The function
 *  may return `undefined` to skip a value of the data source.
 *
 * @yields
 *  The transformed values.
 */
export function *map<RT, VT>(source: Iterable<VT>, fn: (value: VT) => Opt<RT>): IterableIterator<RT> {
    for (const value of source) {
        const result = fn(value);
        if (result !== undefined) { yield result; }
    }
}

/**
 * Creates a new iterator that filters all duplicates of an iterable.
 *
 * @param source
 *  The iterable data source to be unified.
 *
 * @yields
 *  The first occurrence of all different values of an iterable.
 */
export function *unify<VT>(source: Iterable<VT>): IterableIterator<VT> {
    const set = new Set<VT>();
    for (const value of source) {
        if (!set.has(value)) {
            set.add(value);
            yield value;
        }
    }
}

/**
 * Creates a new iterator trying to merge values of an iterable by combining
 * consecutive values into fewer new values.
 *
 * @param source
 *  The iterable data source.
 *
 * @param fn
 *  The aggregator callback function merging two values into one new value.
 *  Receives two consecutive values of the data source. May return a new result
 *  to indicate that the passed values have been merged (in this case, this
 *  function will be called with the merge result and the next value from the
 *  data source again if available), or `undefined` to indicate that the passed
 *  values cannot be merged (in this case, the merge iterator will visit the
 *  _first_ value immediately).
 *
 * @yields
 *  The merged values.
 */
export function *merge<VT>(source: Iterable<VT>, fn: (value1: VT, value2: VT) => Opt<VT>): IterableIterator<VT> {

    // visit all values from the data source
    let merged: Opt<VT>;
    for (const value of source) {

        // no cached result available: remember current value, proceed with next value
        if (merged === undefined) {
            merged = value;
            continue;
        }

        // cached value available (either from callback function, or preceding value form data source)
        const result = fn(merged, value);

        // values have been merged: remember result, try to merge again with next value from data source
        if (result !== undefined) {
            merged = result;
            continue;
        }

        // values cannot be merged: visit cached value, remember current value, and proceed with next value
        yield merged;
        merged = value;
    }

    // visit the stored result (or last value from data source)
    if (merged !== undefined) {
        yield merged;
    }
}
